import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AiGeneratedFlag, Button, Card, CardBody } from "@odingard/design-system";
import type { IngestResult } from "@odingard/sdk-client";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

type Phase = "idle" | "processing" | "done" | "error";

export function CaiUpload() {
  const { activeOrg } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("idle");
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState<IngestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function ingest(file: File) {
    if (!activeOrg) return;
    setFileName(file.name);
    setPhase("processing");
    setError(null);
    try {
      const res = await api.uploadContract(activeOrg.id, file);
      setResult(res);
      setPhase("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
      setPhase("error");
    }
  }

  function reset() {
    setPhase("idle");
    setFileName(null);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Upload &amp; ingestion</h1>
          <p>Add a contract. Contract AI runs real OCR + AI extraction, grounds every field in a citation, and routes fields with low confidence to human review.</p>
        </div>
      </div>

      <Card>
        <CardBody>
          {phase === "idle" && (
            <label
              style={{
                display: "grid",
                placeItems: "center",
                gap: "var(--ods-space-3)",
                padding: "var(--ods-space-8)",
                border: "2px dashed var(--ods-color-line)",
                borderRadius: "var(--ods-radius)",
                cursor: "pointer",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "2rem" }} aria-hidden="true">
                📤
              </div>
              <div>
                <strong>Drop a PDF or click to browse</strong>
                <p style={{ margin: "4px 0 0", color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
                  PDF (digital or scanned) or TXT up to 25 MB. Files are tenant isolated to {activeOrg?.name}.
                </p>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.txt"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void ingest(f);
                }}
              />
            </label>
          )}

          {phase === "processing" && (
            <div>
              <AiGeneratedFlag>Processing {fileName}</AiGeneratedFlag>
              <p style={{ marginTop: "var(--ods-space-4)", color: "var(--ods-color-muted)" }}>
                Uploading, running OCR/text extraction, and analyzing clauses, terms, risk and obligations. This can take
                up to a minute for larger documents.
              </p>
              <div className="progress progress--indeterminate" style={{ marginTop: "var(--ods-space-4)" }}>
                <div className="progress__fill" style={{ width: "40%" }} />
              </div>
            </div>
          )}

          {phase === "done" && result && (
            <div className="ods-state">
              <div className="ods-state__icon" aria-hidden="true">
                ✅
              </div>
              <h3 className="ods-state__title">Extraction complete</h3>
              <p>
                <strong>{result.title}</strong> ({result.type}, counterparty {result.counterparty}) was ingested and
                analyzed by {result.model}. We extracted {result.counts.terms} terms, {result.counts.clauses} clauses and{" "}
                {result.counts.obligations} obligations. {result.counts.reviewQueue} field(s) with low confidence were routed
                to the review queue. Nothing is trusted without human verification.
              </p>
              <div className="ods-row" style={{ justifyContent: "center", marginTop: "var(--ods-space-4)" }}>
                <Button onClick={() => navigate(`/studio/contract-ai/contracts/${result.contractId}`)}>
                  View contract
                </Button>
                {result.counts.reviewQueue > 0 && (
                  <Button variant="secondary" onClick={() => navigate("/studio/contract-ai/review")}>
                    Go to review queue
                  </Button>
                )}
                <Button variant="ghost" onClick={reset}>
                  Upload another
                </Button>
              </div>
            </div>
          )}

          {phase === "error" && (
            <div className="ods-state">
              <div className="ods-state__icon" aria-hidden="true">
                ⚠️
              </div>
              <h3 className="ods-state__title">Ingestion failed</h3>
              <p>{error}</p>
              <div className="ods-row" style={{ justifyContent: "center", marginTop: "var(--ods-space-4)" }}>
                <Button onClick={reset}>Try again</Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
